import React, { useState } from 'react';

import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { useGetReportingLeLens, useUpdateReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useLensCardPreview } from '~/hooks/apis/reporting/useLensCardPreview';

import { isCardLens, LENS_CARD_TYPE_OPTIONS, type Lens } from '~/types/leLens';

import LensCardView from './cards/LensCardView';
import { type LensPresentationEdit, applyLensPresentationEdit } from './presentationEdit';

export default function LensPresentationProposal({
  edit,
  onSavedLens,
  hasUnsavedChanges = false,
}: {
  edit: LensPresentationEdit;
  onSavedLens?: (lens: Lens) => void;
  hasUnsavedChanges?: boolean;
}) {
  const { data, isLoading } = useGetReportingLeLens(edit.lensId);
  const preview = useLensCardPreview();
  const update = useUpdateReportingLeLens();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const lens = data?.lens && isCardLens(data.lens) ? data.lens : undefined;
  const proposed = lens ? applyLensPresentationEdit(lens, edit) : undefined;
  const label = LENS_CARD_TYPE_OPTIONS.find((option) => option.value === edit.type)?.label ?? edit.type;
  if (saved) {
    return <Typography variant="body2">Chart updated</Typography>;
  }
  if (isLoading) {
    return <Typography variant="body2">Loading proposed change…</Typography>;
  }
  if (!lens || !proposed) {
    return <Alert severity="warning">This proposal no longer matches the saved Lens. Ask for the change again.</Alert>;
  }
  if (hasUnsavedChanges) {
    return <Alert severity="info">Save or discard your current card edits before reviewing this change.</Alert>;
  }
  const previewReady = preview.data?.card.status === 'ready' && !preview.data.card.truncated;
  const busy = preview.isPending || update.isPending;
  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        {proposed.title} → {label}
      </Typography>
      {preview.data && (
        <Box sx={{ height: 320, overflow: 'auto' }}>
          <LensCardView
            card={proposed}
            data={preview.data.card}
            datasets={lens.spec.datasets ?? []}
            canEdit={false}
            isFetchingData={false}
            dateRange={{}}
            hasActiveFilters={false}
            onClearFilters={() => {}}
            onDelete={async () => {}}
            onEdit={() => {}}
            onRefresh={() => {}}
            onSelect={() => {}}
            isRefreshing={false}
            insightRefresh={0}
            span={8}
          />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button
          disabled={busy}
          onClick={async () => {
            setError('');
            try {
              await preview.mutateAsync({ lensId: lens.id, version: lens.version, card: proposed, filters: {} });
            } catch (failure) {
              setError(failure instanceof Error ? failure.message : 'Preview failed');
            }
          }}
        >
          {preview.isPending ? 'Preparing preview…' : 'Preview change'}
        </Button>
        <Button
          variant="contained"
          disabled={!previewReady || busy || !data?.access.canEdit}
          onClick={async () => {
            setError('');
            try {
              const result = await update.mutateAsync({
                id: lens.id,
                title: lens.title,
                description: lens.description,
                version: edit.baseVersion,
                spec: {
                  ...lens.spec,
                  cards: lens.spec.cards.map((card) => (card.id === proposed.id ? proposed : card)),
                },
                successMessage: 'Chart updated',
                refetchData: true,
              });
              setSaved(true);
              onSavedLens?.(result.lens);
            } catch (failure) {
              setError(failure instanceof Error ? failure.message : 'Could not save the change');
            }
          }}
        >
          {update.isPending ? 'Saving…' : 'Replace card'}
        </Button>
      </Stack>
    </Stack>
  );
}
